import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { Tables } from '../../../database.types'
import { useBrands } from '@/lib/BrandsProvider'
import NoBrandSelected from '../NoBrandSelected'
import { Skeleton } from "@/components/ui/skeleton"
import { Badge } from "@/components/ui/badge"
import {
  Rss as FeedIcon,
  ChevronDown,
  ChevronUp,
  Newspaper,
  MessageSquare,
  TrendingUp,
  ExternalLink,
  FileText,
  Globe
} from 'lucide-react'
import { Button } from "@/components/ui/button"
import { useNavigate, Link } from 'react-router-dom'
import { cn } from '@/lib/utils'

type Convergence = Tables<'convergences'>
type NewsContent = Tables<'news_contents'>

interface ConvergenceWithRelations extends Convergence {
  brands: {
    id: string;
    brand_name: string;
  };
  news_contents?: NewsContent;
}

interface ConvergenceMetadata {
  ideas?: {
    unique_insights?: string;
    brand_opinion?: string;
    hot_takes?: string;
    controversial_takes?: string;
  };
}

interface InterestingLink {
  url: string;
  image?: string;
  title: string;
  author?: string;
  contents: string;
  publishedDate?: string;
}

const getMetadata = (metadata: any): ConvergenceMetadata | null => {
  if (!metadata || typeof metadata !== 'object') return null;
  return metadata as ConvergenceMetadata;
};

const parseInterestingLinks = (interestingLinks: any): InterestingLink[] => {
  try {
    if (!interestingLinks) return [];
    if (Array.isArray(interestingLinks)) {
      return interestingLinks.filter((item: any) => item && typeof item === 'object' && 'url' in item) as InterestingLink[];
    }
    return [];
  } catch (e) {
    console.error('Error parsing interesting links:', e);
    return [];
  }
};

interface ConvergenceFeedCardProps {
  convergence: ConvergenceWithRelations;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

const ConvergenceFeedCard = ({ convergence, isExpanded, onToggleExpand }: ConvergenceFeedCardProps) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'news' | 'discussion' | 'analysis' | 'content'>('news');
  const [relatedContent, setRelatedContent] = useState<any[]>([]);
  const [contentLoading, setContentLoading] = useState(false);

  const metadata = getMetadata(convergence.metadata);
  const interestingLinks = convergence.news_contents ? parseInterestingLinks(convergence.news_contents.interesting_links) : [];

  // Fetch related content when content tab is activated
  useEffect(() => {
    const fetchRelatedContent = async () => {
      if (activeTab !== 'content' || !isExpanded) return;

      try {
        setContentLoading(true);
        // Fetch content through campaigns that originated from this convergence
        const { data, error } = await supabase
          .from('contents')
          .select(`
            id,
            name,
            content_type,
            status,
            created_at,
            campaign_id,
            campaigns!inner (
              id,
              name,
              originating_convergence_id
            )
          `)
          .eq('campaigns.originating_convergence_id', convergence.id)
          .order('created_at', { ascending: false });

        if (error) throw error;

        setRelatedContent(data || []);
      } catch (error) {
        console.error('Error fetching related content:', error);
        setRelatedContent([]);
      } finally {
        setContentLoading(false);
      }
    };

    fetchRelatedContent();
  }, [activeTab, isExpanded, convergence.id]);

  const toggleExpanded = () => {
    if (!isExpanded) {
      // When expanding, reset to 'news' tab
      setActiveTab('news');
    }
    onToggleExpand();
  };

  const formatDate = (date: string) => {
    const now = new Date();
    const createdAt = new Date(date);
    const diffInHours = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60));
      return diffInMinutes < 1 ? 'Just now' : `${diffInMinutes}m`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h`;
    } else {
      return createdAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const hasNews = convergence.news_contents;
  const hasAnalysis = convergence.news_contents?.trend_analysis ||
    convergence.news_contents?.business_opportunities_analysis ||
    convergence.news_contents?.audience_and_brand_analysis;
  const hasIdeas = metadata?.ideas && (
    metadata.ideas.unique_insights ||
    metadata.ideas.brand_opinion ||
    metadata.ideas.hot_takes ||
    metadata.ideas.controversial_takes ||
    convergence.promo_idea
  );
  const hasContent = true; // Always show content tab

  const hasExpandableContent = hasNews || hasAnalysis || hasIdeas || hasContent;

  return (
    <div className={cn(
      "bg-white transition-all duration-200 ease-in-out mb-6",
      "hover:shadow-lg cursor-pointer rounded-xl overflow-hidden",
      isExpanded && "shadow-2xl border-2 border-blue-500 bg-white z-10"
    )}>
      {/* Main Content */}
      <div
        className="relative"
        onClick={hasExpandableContent ? toggleExpanded : undefined}
      >
        <div className="p-4">
          {/* Header Section - Logo, Brand Name, Metadata */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              {/* Square Logo */}
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                <span className="text-white font-bold text-sm">
                  {convergence.brands.brand_name.substring(0, 2).toUpperCase()}
                </span>
              </div>
              
              {/* Brand Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-bold text-gray-900 text-base hover:underline cursor-pointer">
                    {convergence.brands.brand_name}
                  </span>
                </div>
                <div className="text-gray-500 text-sm mb-1">
                  {/* We could add follower count here if available */}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-500 text-sm">
                    {formatDate(convergence.created_at)}
                  </span>
                  <span className="text-gray-400 text-xs">·</span>
                  <Globe className="w-3.5 h-3.5 text-gray-400" />
                </div>
              </div>
            </div>
            
            {/* Action Button */}
            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
              {convergence.status && (
                <Badge
                  variant={convergence.status === 'Used' ? 'secondary' : 'outline'}
                  className="text-xs"
                >
                  {convergence.status}
                </Badge>
              )}
            </div>
          </div>

          {/* Post Content */}
          <div className="mb-3">
            {/* Title */}
            <Link
              to={`/convergences/${convergence.id}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-block mb-2"
            >
              <h3 className="text-[17px] font-bold text-gray-900 leading-normal hover:underline hover:text-blue-600 cursor-pointer transition-colors">
                {convergence.name}
              </h3>
            </Link>

            {/* Trend Description */}
            <p className="text-[15px] text-gray-700 leading-normal whitespace-pre-wrap">
              {convergence.trend_description}
            </p>
          </div>

          {/* Image - Below content */}
          {interestingLinks.length > 0 && interestingLinks[0].image && (
            <div className="mb-3 rounded-lg overflow-hidden">
              <img
                src={interestingLinks[0].image}
                alt={interestingLinks[0].title || 'Article image'}
                className="w-full object-cover"
                style={{ maxHeight: '500px' }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Convergence Explanation - Below the image */}
          {convergence.convergence && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-700 leading-relaxed">
                {convergence.convergence}
              </p>
            </div>
          )}

          {/* Expand/Collapse Button */}
          {hasExpandableContent && (
            <div className="mt-5">
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "h-9 px-4 text-sm font-medium transition-all border-2 rounded-lg",
                  isExpanded
                    ? "bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100 hover:border-blue-400 shadow-sm"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400 shadow-sm"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpanded();
                }}
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-1.5" />
                    Show less
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-1.5" />
                    Show more
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Expandable Tabbed Content */}
      {isExpanded && hasExpandableContent && (
        <div className="bg-gray-100 border-t border-gray-300">
          {/* Tab Navigation */}
          <div className="flex items-center border-b border-gray-300 bg-gray-100">
            {hasNews && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTab('news');
                }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all duration-200 border-b-2 ${
                  activeTab === 'news'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Newspaper className="h-4 w-4" />
                <span>News</span>
              </button>
            )}

            {hasIdeas && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTab('discussion');
                }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all duration-200 border-b-2 ${
                  activeTab === 'discussion'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <MessageSquare className="h-4 w-4" />
                <span>Discussion</span>
              </button>
            )}

            {hasAnalysis && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTab('analysis');
                }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all duration-200 border-b-2 ${
                  activeTab === 'analysis'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <TrendingUp className="h-4 w-4" />
                <span>Analysis</span>
              </button>
            )}

            {hasContent && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTab('content');
                }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all duration-200 border-b-2 ${
                  activeTab === 'content'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FileText className="h-4 w-4" />
                <span>Your Content</span>
              </button>
            )}
          </div>

          {/* Tab Content */}
          {activeTab === 'news' && hasNews && convergence.news_contents && (
            <div className="p-4 bg-gray-50">
            <div className="space-y-4">
              {/* Authoritative URLs */}
              {convergence.news_contents.authoritative_urls && Array.isArray(convergence.news_contents.authoritative_urls) && (
                <div>
                  <div className="space-y-2">
                    {convergence.news_contents.authoritative_urls.map((item: any, index: number) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-white rounded-lg border border-gray-200">
                        <ExternalLink className="h-4 w-4 text-blue-500 flex-shrink-0" />
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline truncate"
                        >
                          {item.title || item.url}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Interesting Links */}
              {interestingLinks.length > 0 && (
                <div>
                  <div className="space-y-3">
                    {interestingLinks.map((item, index) => (
                      <div key={index} className="bg-white p-4 rounded-lg border border-gray-200">
                        <div className="flex gap-3">
                          {item.image && (
                            <img
                              src={item.image}
                              alt={item.title || 'Article image'}
                              className="w-16 h-16 object-cover rounded flex-shrink-0"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            {item.title && (
                              <h6 className="font-medium text-sm text-gray-900 mb-1 line-clamp-2">{item.title}</h6>
                            )}
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline truncate block mb-1"
                            >
                              {item.url}
                            </a>
                            {item.contents && (
                              <p className="text-xs text-gray-600 line-clamp-2">{item.contents}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </div>
            </div>
          )}

          {/* Analysis Section */}
          {activeTab === 'analysis' && hasAnalysis && (
            <div className="p-4 bg-gray-50">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="h-4 w-4 text-gray-600" />
                <h4 className="text-sm font-semibold text-gray-900">Analysis</h4>
              </div>

            <div className="space-y-4">
              {convergence.news_contents?.trend_analysis && (
                <div>
                  <h5 className="font-medium text-sm text-gray-700 mb-2">Trend Analysis</h5>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{convergence.news_contents.trend_analysis}</p>
                </div>
              )}

              {convergence.news_contents?.business_opportunities_analysis && (
                <div>
                  <h5 className="font-medium text-sm text-gray-700 mb-2">Business Opportunities</h5>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{convergence.news_contents.business_opportunities_analysis}</p>
                </div>
              )}

              {convergence.news_contents?.audience_and_brand_analysis && (
                <div>
                  <h5 className="font-medium text-sm text-gray-700 mb-2">Audience & Brand Analysis</h5>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{convergence.news_contents.audience_and_brand_analysis}</p>
                </div>
              )}
              </div>
            </div>
          )}

          {/* Discussion Section - Twitter-style Thread */}
          {activeTab === 'discussion' && hasIdeas && (
            <div className="bg-gray-50 px-4 py-3">

            {/* Connecting line and indented comments */}
            <div className="relative pl-8 space-y-3 before:content-[''] before:absolute before:left-3 before:top-0 before:bottom-0 before:w-0.5 before:bg-gray-300">
              {metadata?.ideas?.unique_insights && (
                <div className="flex gap-3 bg-white p-3 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
                      <span className="text-amber-700 text-sm font-semibold">AI</span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm text-gray-900">AI Insights</span>
                      <span className="text-gray-400 text-xs">·</span>
                      <span className="text-gray-500 text-xs">Just now</span>
                    </div>
                    <p className="text-[15px] text-gray-700 whitespace-pre-wrap leading-normal">{metadata.ideas.unique_insights}</p>
                  </div>
                </div>
              )}

              {metadata?.ideas?.brand_opinion && (
                <div className="flex gap-3 bg-white p-3 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                      <span className="text-blue-700 text-sm font-semibold">BP</span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm text-gray-900">Brand Perspective</span>
                      <span className="text-gray-400 text-xs">·</span>
                      <span className="text-gray-500 text-xs">Just now</span>
                    </div>
                    <p className="text-[15px] text-gray-700 whitespace-pre-wrap leading-normal">{metadata.ideas.brand_opinion}</p>
                  </div>
                </div>
              )}

              {metadata?.ideas?.hot_takes && (
                <div className="flex gap-3 bg-white p-3 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center">
                      <span className="text-orange-700 text-sm font-semibold">HT</span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm text-gray-900">Hot Take</span>
                      <span className="text-gray-400 text-xs">·</span>
                      <span className="text-gray-500 text-xs">Just now</span>
                    </div>
                    <p className="text-[15px] text-gray-700 whitespace-pre-wrap leading-normal">{metadata.ideas.hot_takes}</p>
                  </div>
                </div>
              )}

              {metadata?.ideas?.controversial_takes && (
                <div className="flex gap-3 bg-white p-3 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center">
                      <span className="text-red-700 text-sm font-semibold">CT</span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm text-gray-900">Controversial Take</span>
                      <span className="text-gray-400 text-xs">·</span>
                      <span className="text-gray-500 text-xs">Just now</span>
                    </div>
                    <p className="text-[15px] text-gray-700 whitespace-pre-wrap leading-normal">{metadata.ideas.controversial_takes}</p>
                  </div>
                </div>
              )}

              {convergence.promo_idea && (
                <div className="flex gap-3 bg-white p-3 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center">
                      <span className="text-purple-700 text-sm font-semibold">PR</span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm text-gray-900">Promo Idea</span>
                      <span className="text-gray-400 text-xs">·</span>
                      <span className="text-gray-500 text-xs">Just now</span>
                    </div>
                    <p className="text-[15px] text-gray-700 whitespace-pre-wrap leading-normal">{convergence.promo_idea}</p>
                  </div>
                </div>
              )}
              </div>
            </div>
          )}

          {/* Content Section */}
          {activeTab === 'content' && (
            <div className="p-4 bg-gray-50">
              {/* Content Display */}
              {contentLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-20" />
                  <Skeleton className="h-20" />
                </div>
              ) : relatedContent.length === 0 ? (
                <div className="bg-white p-8 rounded-lg border border-gray-200 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <FileText className="h-6 w-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-600 mb-1">No content created yet</p>
                    <p className="text-xs text-gray-500">
                      Create content from this convergence to get started
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {relatedContent.map((content) => (
                    <div
                      key={content.id}
                      className="bg-white p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/content/${content.id}`);
                      }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h5 className="font-medium text-sm text-gray-900 mb-1 truncate">
                            {content.name}
                          </h5>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Badge variant="outline" className="text-xs">
                              {content.content_type}
                            </Badge>
                            {content.campaigns && (
                              <>
                                <span>·</span>
                                <span className="truncate">
                                  {content.campaigns.name}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex-shrink-0 flex items-center gap-2">
                          <Badge
                            variant={content.status === 'published' ? 'secondary' : 'outline'}
                            className="text-xs"
                          >
                            {content.status}
                          </Badge>
                          {content.created_at && (
                            <span className="text-xs text-gray-500">
                              {new Date(content.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric'
                              })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function ConvergenceFeed() {
  const { selectedBrand } = useBrands();
  const [convergences, setConvergences] = useState<ConvergenceWithRelations[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);

  useEffect(() => {
    const fetchConvergences = async () => {
      if (!selectedBrand) return;

      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('convergences')
          .select(`
            *,
            brands!inner (
              id,
              brand_name
            ),
            news_contents (
              id,
              name,
              description,
              trend_analysis,
              business_opportunities_analysis,
              audience_and_brand_analysis,
              raw_headline,
              raw_content,
              authoritative_urls,
              interesting_links
            )
          `)
          .eq('brand_id', selectedBrand.id)
          .order('created_at', { ascending: false });

        if (error) throw error;

        setConvergences(data as unknown as ConvergenceWithRelations[] || []);
      } catch (error) {
        console.error('Error fetching convergences:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConvergences();

    // Set up real-time subscription
    if (selectedBrand) {
      const subscription = supabase
        .channel('convergences-feed')
        .on('postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'convergences',
            filter: `brand_id=eq.${selectedBrand.id}`
          },
          () => {
            // Refetch to get the full data with relations
            fetchConvergences();
          }
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [selectedBrand]);

  if (!selectedBrand) {
    return <NoBrandSelected />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto max-w-2xl">
        {/* Feed Content */}
        <div className="border-x border-gray-200 bg-white">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="border-b border-gray-200 p-4">
                <Skeleton className="h-32" />
              </div>
            ))
          ) : convergences.length === 0 ? (
            <div className="border-b border-gray-200 p-12 text-center">
              <div className="flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <FeedIcon className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">
                  No convergences yet
                </h3>
                <p className="text-gray-600 text-sm max-w-md">
                  Convergences will appear here as they are discovered for your brand. Check back soon!
                </p>
              </div>
            </div>
          ) : (
            convergences.map((convergence, index) => (
              <div key={convergence.id} className={index !== convergences.length - 1 ? 'border-b border-gray-200' : ''}>
                <ConvergenceFeedCard
                  convergence={convergence}
                  isExpanded={expandedCardId === convergence.id}
                  onToggleExpand={() => setExpandedCardId(expandedCardId === convergence.id ? null : convergence.id)}
                />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
